/**
 * Design system -- brand & status color palette.
 *
 * Centralized color definitions for the application (moved from the
 * former `styles/colorPalette.ts`, values unchanged). Update colors here
 * to change the theme across the app.
 *
 * For the shadcn/ui-style semantic tokens (`--primary`, `--border`, etc.
 * consumed via Tailwind classes like `bg-primary`), see `tokens.css`.
 */

export const colors = {
  // Header colors
  header: '#132179',

  // Sidebar colors
  sidebar: '#0c1344',
  sidebarHover: '#395a89', // Lighter hover color for sidebar navigation
  sidebarActive: '#4c6993',
  sidebarText: '#e2e7ec', // Same as tableBorder

  // Button colors
  buttonPrimary: '#ed5900',
  buttonPrimaryHover: '#ff7b33', // Lighter orange for hover
  buttonSecondary: '#3f51b5', // Same as sidebarHover
  buttonSecondaryHover: '#6070c2', // Lighter blue-gray for hover
  buttonTertiary: '#ffffff', // White
  buttonTertiaryHover: '#d9dfec', // hover color for white/tertiary buttons
  buttonTextWhite: '#ffffff', // White font color for buttons

  // Completed-state tint for tiles/cards: the hover blue (buttonSecondaryHover) at ~12% over white,
  // so a resolved item reads at a glance without competing with the content.
  tileCompletedBg: '#eceef8',

  // Table colors
  tableBorder: '#e2e7ec',

  // Form colors
  formHeaderFooter: '#e6eaf3', // Very light grayish blue for form headers and footers

  // Schedule/Gantt colors
  scheduleWeekendBg: '#f1f3f9', // Lighter tint of formHeaderFooter -- subtle weekend (Sat/Sun) column shading across Schedule

  // Tag colors - Status (text colors)
  tagStatusDraft: '#374151', // gray-700
  tagStatusSent: '#2563eb', // blue-600
  tagStatusApproved: '#16a34a', // green-600
  tagStatusRejected: '#dc2626', // red-600
  tagStatusPending: '#ca8a04', // yellow-600
  tagStatusInProgress: '#9333ea', // purple-600 (purple for in progress)
  tagStatusCompleted: '#2563eb', // blue-600 (blue for completed)
  tagStatusActive: '#16a34a', // green-600
  tagStatusInactive: '#ca8a04', // yellow-600
  tagStatusCancelled: '#b91c1c', // red-700 (darker red for cancelled)
  tagStatusSubmitted: '#2563eb', // blue-600 (submitted status)
  tagStatusOrdered: '#7c3aed', // violet-600 (ordered status)
  tagStatusReceived: '#2563eb', // blue-600 (received status - darker blue, same as sent/completed)
  tagStatusOpen: '#dc2626', // red-600 (open warranty - same as rejected)
  tagStatusResolved: '#16a34a', // green-600 (resolved warranty - same as approved)
  tagStatusUnderReview: '#ca8a04', // yellow-600 (under review - same as pending)
  tagStatusAwarded: '#16a34a', // green-600 (awarded - same as approved)
  tagStatusPaid: '#16a34a', // green-600 (paid bills - same as approved)
  tagStatusPartiallyPaid: '#f59e0b', // amber-600 (partially paid bills - yellow/orange)
  tagStatusVoid: '#9333ea', // violet-600 (void bills - purple)

  // Tag colors - Status (background colors - very light)
  tagStatusDraftBg: '#f3f4f6', // gray-100
  tagStatusSentBg: '#dbeafe', // blue-100
  tagStatusApprovedBg: '#dcfce7', // green-100
  tagStatusRejectedBg: '#fee2e2', // red-100
  tagStatusPendingBg: '#fef9c3', // yellow-100
  tagStatusInProgressBg: '#f3e8ff', // purple-100 (light purple background for in progress)
  tagStatusCompletedBg: '#dbeafe', // blue-100 (light blue background for completed)
  tagStatusActiveBg: '#dcfce7', // green-100
  tagStatusInactiveBg: '#fef9c3', // yellow-100
  tagStatusCancelledBg: '#fee2e2', // red-100 (light red background for cancelled)
  tagStatusSubmittedBg: '#dbeafe', // blue-100 (light blue background for submitted)
  tagStatusOrderedBg: '#ede9fe', // violet-100 (light purple background for ordered)
  tagStatusReceivedBg: '#dbeafe', // blue-100 (light blue background for received, same as sent/completed)
  tagStatusOpenBg: '#fee2e2', // red-100 (open warranty - same as rejected)
  tagStatusResolvedBg: '#dcfce7', // green-100 (resolved warranty - same as approved)
  tagStatusUnderReviewBg: '#fef9c3', // yellow-100 (under review - same as pending)
  tagStatusAwardedBg: '#dcfce7', // green-100 (awarded - same as approved)
  tagStatusPaidBg: '#dcfce7', // green-100 (paid bills - same as approved)
  tagStatusPartiallyPaidBg: '#fef3c7', // amber-100 (partially paid bills - light yellow/orange)
  tagStatusVoidBg: '#f3e8ff', // violet-100 (void bills - light purple)

  // Lead-specific status colors (text colors)
  tagStatusActiveClient: '#059669', // emerald-600 (success/active client)
  tagStatusNewLead: '#0284c7', // sky-600 (fresh/new lead)
  tagStatusContacted: '#f59e0b', // amber-600 (contacted/attention)
  tagStatusQualified: '#0891b2', // cyan-600 (qualified/ready)
  tagStatusProposal: '#9333ea', // purple-600 (proposal stage)
  tagStatusNegotiation: '#ea580c', // orange-600 (negotiating)
  tagStatusClosedWon: '#15803d', // green-700 (won/success)
  tagStatusClosedLost: '#dc2626', // red-600 (lost/failure)

  // Proposal-specific status colors (text colors)
  tagStatusFeedback: '#7c3aed', // violet-600 (purple for feedback)
  tagStatusRevision: '#ca8a04', // yellow-600 (yellow for revision)

  // Lead-specific status colors (background colors - very light)
  tagStatusActiveClientBg: '#d1fae5', // emerald-100
  tagStatusNewLeadBg: '#e0f2fe', // sky-100
  tagStatusContactedBg: '#fef3c7', // amber-100
  tagStatusQualifiedBg: '#cffafe', // cyan-100
  tagStatusProposalBg: '#f3e8ff', // purple-100
  tagStatusNegotiationBg: '#ffedd5', // orange-100
  tagStatusClosedWonBg: '#dcfce7', // green-100
  tagStatusClosedLostBg: '#fee2e2', // red-100

  // Proposal-specific status colors (background colors - very light)
  tagStatusFeedbackBg: '#ede9fe', // violet-100 (purple background)
  tagStatusRevisionBg: '#fef9c3', // yellow-100 (yellow background)

  // Tag colors - Priority (text colors)
  tagPriorityLow: '#16a34a', // green-600 (green for low priority)
  tagPriorityMedium: '#ca8a04', // yellow-600 (yellow for medium priority)
  tagPriorityHigh: '#dc2626', // red-600

  // Tag colors - Priority (background colors - very light)
  tagPriorityLowBg: '#dcfce7', // green-100 (light green)
  tagPriorityMediumBg: '#fef9c3', // yellow-100 (light yellow)
  tagPriorityHighBg: '#fee2e2', // red-100

  // Tag colors - Role (text colors)
  tagRoleAdmin: '#9333ea', // purple-600 (purple for admin)
  tagRoleMember: '#cf522f', // orange (same as buttonPrimary for member)

  // Tag colors - Role (background colors - very light)
  tagRoleAdminBg: '#f3e8ff', // purple-100 (light purple background for admin)
  tagRoleMemberBg: '#ffedd5', // orange-100 (light orange background for member)

  // KPI Warning Colors - Light orange background for KPIs that are not OK
  kpiWarningBg: 'rgba(207, 82, 47, 0.16)', // Light orange background (buttonPrimary with 16% opacity)

  // Dashboard Tile Health Colors - Separate from button colors for independent control
  // Kept in sync with tokens.css's --dashboard-tile-good/-bad (that CSS-side
  // duplication is unavoidable: index.css's .dashboard-tile-good/-bad
  // classes need a real CSS value, and these two are consumed as inline
  // `style={{}}` colors elsewhere in JS/TS, so one pair of literals has to
  // exist in both worlds without a build-time bridge between them).
  dashboardTileHealthGood: '#e6eaf3', // Blueish color for healthy/good KPI tiles (same as formHeaderFooter)
  dashboardTileHealthBad: '#f7e3de', // Orange background for KPIs that are not OK (rgba(207, 82, 47, 0.16) converted to hex)

  // Dropdown / list-menu colors - generic rule for every combobox-style
  // dropdown in the app: change these here once, instead of each dropdown
  // picking its own shade.
  dropdownMutedText: '#9ca3af', // gray-400 - empty/no-results state text
  dropdownText: '#374151', // gray-700 - normal dropdown item text (e.g. "Add new client")
  // dropdownDivider removed (2026-09-25, border standardization): border
  // color belongs to tokens.css's `--border` (consumed via the
  // `border-border` Tailwind class), not to this brand/status palette --
  // its one consumer (ClientLeadCombobox.tsx's "Add new" divider) now
  // uses `border-border` directly, same token every other bordered
  // container/field in the app uses.
} as const;
